import { useState } from "react";
import { Stack, Separator } from "@fluentui/react";
import { ToggleSection } from "../ToggleSection";
import { ChipDropdown } from "../ChipDropdown";

import styles from "./FilterSettings.module.css";

interface FilterValue {
    text: string;
    key: string;
}

interface Filter {
    name: string;
    displayName: string;
    values: FilterValue[];
}

export type KnowledgeSource = "sharepoint" | "lorinlogs" | "lorinaccess" | "lorintasks" | "quicksearch";

interface Props {
    filters: Filter[];
    selectedFilters: Record<string, string[]>;
    updateFilters: (filters: Record<string, string[]>) => void;
    knowledgeSources?: KnowledgeSource[];
    selectedKnowledgeSources?: KnowledgeSource[];
    onKnowledgeSourcesChange?: (sources: KnowledgeSource[]) => void;
    onClearFilters?: () => void;
    availableValues?: Record<string, Record<string, number>>;
    totalDocuments?: number;
    isLoadingFilterOptions?: boolean;
}

export const FilterSettings = ({
    filters,
    selectedFilters,
    updateFilters,
    knowledgeSources = [],
    selectedKnowledgeSources = [],
    onKnowledgeSourcesChange,
    onClearFilters,
    availableValues,
    totalDocuments,
    isLoadingFilterOptions
}: Props) => {
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);

    const handleFilterChange = (filterName: string, newKeys: string[]) => {
        updateFilters({ ...selectedFilters, [filterName]: newKeys });
    };

    const handleSourceToggle = (source: KnowledgeSource, checked: boolean) => {
        if (!onKnowledgeSourcesChange) return;
        if (checked) {
            onKnowledgeSourcesChange([...selectedKnowledgeSources, source]);
        } else {
            onKnowledgeSourcesChange(selectedKnowledgeSources.filter(s => s !== source));
        }
    };

    const sourceFileFilter = filters.find(f => f.name.toLowerCase().includes("source"));
    const documentCount = sourceFileFilter ? sourceFileFilter.values.length : 0;

    const hasKnowledgeBaseToggle = !!onKnowledgeSourcesChange;

    const renderFilter = (filter: Filter) => {
        const selectedKeys = selectedFilters[filter.name] || [];
        const sortedOptions = filter.values
            .slice()
            .sort((a, b) => a.text.localeCompare(b.text))
            .map(v => ({ key: v.key, text: v.text }));
        return (
            <ChipDropdown
                key={filter.name}
                label={filter.displayName}
                options={sortedOptions}
                selectedKeys={selectedKeys}
                placeholder={`All ${filter.displayName.toLowerCase()}s selected`}
                onChange={keys => handleFilterChange(filter.name, keys)}
                isOpen={openDropdown === filter.name}
                onOpenChange={open => setOpenDropdown(open ? filter.name : null)}
            />
        );
    };

    if (!hasKnowledgeBaseToggle) {
        return <Stack tokens={{ childrenGap: 10 }}>{filters.map(filter => renderFilter(filter))}</Stack>;
    }

    const visibleSections = [
        knowledgeSources.includes("sharepoint") ? (
            <ToggleSection
                key="sharepoint"
                label="SharePoint documents"
                isOn={selectedKnowledgeSources.includes("sharepoint")}
                onToggle={checked => handleSourceToggle("sharepoint", checked)}
            >
                <Stack tokens={{ childrenGap: 10 }}>
                    <span className={styles.filtersLabel}>Filters</span>
                    {filters.map(filter => renderFilter(filter))}
                    <span className={styles.resultsCount}>
                        {isLoadingFilterOptions
                            ? "Results: ..."
                            : `Results: ${totalDocuments !== undefined ? totalDocuments : documentCount} document${totalDocuments !== 1 ? "s" : ""}`}
                    </span>
                    {onClearFilters && (
                        <button className={styles.clearFiltersButton} onClick={onClearFilters}>
                            CLEAR FILTERS
                        </button>
                    )}
                </Stack>
            </ToggleSection>
        ) : null,
        knowledgeSources.includes("lorinlogs") ? (
            <ToggleSection
                key="lorinlogs"
                label="Lorin"
                isOn={selectedKnowledgeSources.includes("lorinlogs")}
                onToggle={checked => handleSourceToggle("lorinlogs", checked)}
            />
        ) : null,
        knowledgeSources.includes("lorinaccess") ? (
            <ToggleSection
                key="lorinaccess"
                label="Lorin Access"
                isOn={selectedKnowledgeSources.includes("lorinaccess")}
                onToggle={checked => handleSourceToggle("lorinaccess", checked)}
            />
        ) : null,
        knowledgeSources.includes("lorintasks") ? (
            <ToggleSection
                key="lorintasks"
                label="LorinTasks"
                isOn={selectedKnowledgeSources.includes("lorintasks")}
                onToggle={checked => handleSourceToggle("lorintasks", checked)}
            />
        ) : null,
        knowledgeSources.includes("quicksearch") ? (
            <ToggleSection
                key="quicksearch"
                label="Quick search"
                isOn={selectedKnowledgeSources.includes("quicksearch")}
                onToggle={checked => handleSourceToggle("quicksearch", checked)}
            />
        ) : null
    ].filter(Boolean);

    return (
        <Stack tokens={{ childrenGap: 0 }}>
            {visibleSections.flatMap((section, index) =>
                index < visibleSections.length - 1 ? [section, <Separator key={`separator-${index}`} />] : [section]
            )}
        </Stack>
    );
};
